import localeText from '@/locales/datagrid'
import { deleteNullish, onlyDefined } from '@acme/common'
import {
    CellContext,
    createColumnHelper,
    DisplayColumnDef,
} from '@tanstack/react-table'
import { CheckIcon, XIcon } from 'lucide-react'
import { optionsColumn } from '../../hooks/useDataGridPro'
import { useTableApiContext } from '../../utils/table-context'
import {
    ColumnDefPro,
    GridColDefPro,
    GridRowId,
    ValidRowModel,
} from '../../utils/types'
import { renderActions } from './ActionsCell'
import {
    CheckboxSelectionCell,
    CheckboxSelectionHeaderCell,
    SELECTION_FIELD,
} from './CheckboxSelection'
import EditableCell from './EditableCell'

export function translateColDef(oldColDef: GridColDefPro[] = []) {
    const { checkboxSelection, columnGroupingModel } = useTableApiContext()
    const columnHelper = createColumnHelper()

    if (
        checkboxSelection &&
        !oldColDef.map(it => it.field).includes(SELECTION_FIELD)
    ) {
        oldColDef.unshift({
            ...optionsColumn,
            field: '__selected__',
            type: 'boolean',
            renderCell: CheckboxSelectionCell,
            renderHeader: CheckboxSelectionHeaderCell,
        })
    }

    const visibleColumns = oldColDef.filter(colDef => !colDef.hide)

    function processColumn(colDef: GridColDefPro) {
        const visibleIndex = visibleColumns.findIndex(
            it => it.field === colDef.field,
        )
        if (colDef.hide) return null
        if (colDef.type === 'actions')
            visibleColumns[visibleIndex] = {
                ...optionsColumn,
                ...colDef,
            }
        if (colDef.renderCell) {
            colDef._hasCustomRenderCell = true
            visibleColumns[visibleIndex] = {
                align: 'center',
                ...colDef,
            }
        }
        return columnHelper.accessor(
            (
                /* ValidRowModel (row original) */
                row: any,
            ) => {
                let value = row[colDef.field]
                if (colDef.valueGetter)
                    value = colDef.valueGetter({
                        row,
                        field: colDef.field,
                        id: row.id,
                        value,
                    })
                return value
            },

            deleteNullish({
                id: colDef.field,
                cell: (params: CellContext<ValidRowModel, any>) => {
                    let value = params.cell.getValue()
                    const row = params.row.original,
                        id = (row.id ?? params.row.id) as GridRowId,
                        field = colDef.field

                    const isEditable =
                        typeof colDef.editable === 'function'
                            ? colDef.editable({ field, id, row })
                            : colDef.editable

                    switch (colDef.type) {
                        case 'actions':
                            colDef._hasCustomRenderCell = true
                            return renderActions(
                                colDef.getActions?.({
                                    field,
                                    id,
                                    row,
                                }),
                            )
                        case 'boolean':
                            if (isEditable) {
                                colDef._hasCustomRenderCell = true
                                return (
                                    <EditableCell
                                        {...{
                                            type: colDef.type,
                                            defaultValue: value,
                                            field,
                                            id,
                                            row: row as any,
                                        }}
                                    />
                                )
                            }
                            switch (value) {
                                case true:
                                    colDef._hasCustomRenderCell = true
                                    return (
                                        <caption
                                            title={localeText.cellValueTrue}
                                            className="flex justify-center items-center"
                                        >
                                            <CheckIcon />
                                        </caption>
                                    )
                                case false:
                                    colDef._hasCustomRenderCell = true
                                    return (
                                        <caption
                                            title={localeText.cellValueFalse}
                                            className="flex justify-center items-center"
                                        >
                                            <XIcon />
                                        </caption>
                                    )
                                default:
                                    return undefined
                            }
                        default:
                            if (isEditable) {
                                colDef._hasCustomRenderCell = true
                                return (
                                    <EditableCell
                                        {...{
                                            type: colDef.type,
                                            defaultValue: value,
                                            field,
                                            id,
                                            row: row as any,
                                        }}
                                    />
                                )
                            }
                            if (colDef.valueFormatter)
                                value = colDef.valueFormatter({
                                    field,
                                    value,
                                    id,
                                })
                            return value
                    }
                },
                header: colDef.headerName,
                size: colDef.width,
                minSize: colDef.minWidth,
                enableColumnFilter: colDef.filterable !== false,
                enableHiding: colDef.hideable !== false,
                enableSorting: colDef.sortable !== false,
            }) as DisplayColumnDef<unknown>,
        )
    }

    const proccessedColumns = visibleColumns
        .map(processColumn)
        .filter(onlyDefined)
        .map(assignDefs(oldColDef))

    if (columnGroupingModel) {
        const groupedColumns = columnGroupingModel.map(group => {
            return columnHelper.group({
                header: group.groupId,
                // @ts-expect-error _ogDef
                columns: proccessedColumns
                    .filter(col => group.children.includes(col._ogDef.field))
                    .map(assignDefs(oldColDef)),
            })
        })

        // Find the index of the first grouped column
        const firstGroupedColumnIndex = proccessedColumns.findIndex(col =>
            columnGroupingModel
                .flatMap(group => group.children)
                .includes(col._ogDef.field),
        )

        // Filter out grouped columns
        const nonGroupedColumns = proccessedColumns.filter(
            col =>
                !columnGroupingModel
                    .flatMap(group => group.children)
                    .includes(col._ogDef.field),
        )

        // Prepare grouped columns with assigned definitions
        const finalGroupedColumns = groupedColumns.map(
            assignDefs(
                columnGroupingModel.map(it => ({
                    ...it,
                    field: it.groupId,
                })),
            ),
        )

        // Insert grouped columns at the first grouped column index
        if (firstGroupedColumnIndex >= 0) {
            const insertIndex = Math.min(
                firstGroupedColumnIndex,
                nonGroupedColumns.length,
            )
            return [
                ...nonGroupedColumns.slice(0, insertIndex),
                ...finalGroupedColumns,
                ...nonGroupedColumns.slice(insertIndex),
            ]
        }

        // If no grouped columns found, just return non-grouped columns with grouped ones at the end
        return nonGroupedColumns.concat(finalGroupedColumns)
    }
    return proccessedColumns
}

function assignDefs(defs: GridColDefPro[]) {
    return function (item: any): ColumnDefPro {
        const def = defs.find(
            d => d.field === item.id || d.headerName === item.id,
        )
        return Object.assign(item, {
            _ogDef: {
                ...def,
                align: def?.align || 'left',
                headerAlign: def?.headerAlign || 'left',
                disableColumnMenu: def?.disableColumnMenu ?? false,
                colSpan: def?.colSpan ?? 1,
                editable: def?.editable ?? false,
            },
        }) as ColumnDefPro
    }
}
